import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { handleAlert } from "redux/festures/alertSlice";
import http from "Utils/api";
import { useDebounceHook } from "hooks/useDebounce";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import SkLoading from "components/SkLoading";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Card from "@mui/material/Card";
import { Icon, Pagination, Stack } from "@mui/material";
import DataTable from "examples/Tables/DataTable";
import { useMaterialUIController } from "context";

const columns = [
  { Header: "S.No", accessor: "no", width: "10%" },
  { Header: "Partner Details", accessor: "partnerDetails", width: "25%" },
  { Header: "Contact Info", accessor: "contactInfo", width: "20%" },
  { Header: "Address", accessor: "address", width: "25%" },
  { Header: "Wallet Info", accessor: "walletInfo", width: "15%" },
  { Header: "Status", accessor: "status", width: "5%" },
];

const PartnerSettlement = () => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const dispatch = useDispatch();
  const adminId = localStorage.getItem("admin_id");

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 1,
  });

  const ITEMS_PER_PAGE = 10;
  
  // Debounce search input to prevent multiple API calls
  const debouncedSearchKey = useDebounceHook(searchKey, 500);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const response = await http.get(`/partners?page=${currentPage}&limit=${ITEMS_PER_PAGE}${debouncedSearchKey ? `&search=${debouncedSearchKey}` : ""}`);
      
      if (response.data.success) {
        setPartners(response.data.data);
        setPagination(response.data.pagination);
      } else {
        dispatch(
          handleAlert({
            isOpen: true,
            type: "error",
            msg: response.data.message || "Failed to fetch partners",
          })
        );
      }
    } catch (error) {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "error",
          msg: error.response?.data?.message || "Error fetching partners",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, [currentPage, debouncedSearchKey]);

  const handleSearch = (value) => {
    setSearchKey(value);
    setCurrentPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const rowsData = partners.map((partner, index) => ({
    no: (
      <MDTypography sx={{ fontSize: 12, fontWeight: "medium" }} variant="text">
        {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
      </MDTypography>
    ),
    partnerDetails: (
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <MDTypography sx={{ fontSize: 12, fontWeight: "medium" }} variant="text">
          {partner.name || "N/A"}
        </MDTypography>
        <MDTypography sx={{ fontSize: 10 }} variant="text">
          ID: {partner._id}
        </MDTypography>
      </MDBox>
    ),
    contactInfo: (
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {partner.email && (
          <MDTypography sx={{ fontSize: 11 }} variant="text">
            {partner.email}
          </MDTypography>
        )}
        <MDTypography sx={{ fontSize: 11 }} variant="text">
          {partner.phoneNumber || "N/A"}
        </MDTypography>
      </MDBox>
    ),
    address: (
      <MDTypography
        sx={{ fontSize: 11 }}
        variant="text"
        style={{
          maxWidth: "200px",
          lineHeight: "14px",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {partner.address || "N/A"}
      </MDTypography>
    ),
    walletInfo: (
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <MDTypography sx={{ fontSize: 11, fontWeight: "medium" }} variant="text">
          Balance: {formatCurrency(partner.wallet?.balance || 0)}
        </MDTypography>
        <MDTypography sx={{ fontSize: 10,  }} variant="text">
          Total Earnings: {formatCurrency(partner.wallet?.totalEarning || 0)}
        </MDTypography>
        <MDTypography sx={{ fontSize: 10,  }} variant="text">
          Total Withdrawn: {formatCurrency(partner.wallet?.totalWithdrawn || 0)}
        </MDTypography>
        {partner.wallet?.lastSettlementDate && (
          <MDTypography sx={{ fontSize: 10, }} variant="text">
            Last Settled: {formatDate(partner.wallet.lastSettlementDate)}
          </MDTypography>
        )}
      </MDBox>
    ),
    status: (
      <MDBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <MDBox
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: partner.disable ? "error.main" : "success.main",
          }}
        />
        <MDTypography sx={{ fontSize: 10 }} variant="text">
          {partner.disable ? "Disabled" : "Active"}
        </MDTypography>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <MDBox py={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <MDTypography variant="h6" color="white">
              Partner Settlement
            </MDTypography>
            <MDButton
              variant="gradient"
              color="dark"
              onClick={fetchPartners}
              disabled={loading}
            >
              <Icon fontSize="small">refresh</Icon>
              &nbsp; Refresh
            </MDButton>
          </MDBox>

          {/* Search Section */}
          <MDBox px={3} py={2}>
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <MDBox sx={{ flex: 1, maxWidth: 400 }}>
                <MDInput
                  placeholder="Search by name, email, or phone..."
                  type="text"
                  fullWidth
                  value={searchKey}
                  onChange={(e) => handleSearch(e.target.value)}
                  disabled={loading}
                  startAdornment={
                    <Icon fontSize="small" sx={{ mr: 1, color: "text.secondary" }}>
                      search
                    </Icon>
                  }
                />
              </MDBox>
              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <MDTypography variant="button" color="text.secondary">
                  Total Partners: {pagination.total}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* Table Section */}
          <MDBox py={3}>
            {loading ? (
              <SkLoading />
            ) : partners && partners.length > 0 ? (
              <>
                <DataTable
                  table={{
                    columns: columns,
                    rows: rowsData,
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  noEndBorder
                  canSearch={false}
                  showTotalEntries={false}
                  pagination={false}
                  isPagination={false}
                />
                
                {/* Pagination */}
                {pagination.pages > 1 && (
                  <MDBox
                    sx={{
                      mt: 5,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Stack spacing={2} direction="row">
                      <Pagination
                        sx={({ palette: { dark, white, info } }) => ({
                          "& .MuiPaginationItem-text": {
                            color: darkMode ? white.main : dark.main,
                          },
                          "& .MuiPaginationItem-icon": {
                            color: darkMode ? white.main : dark.main,
                          },
                          "& .MuiPaginationItem-textInfo": {
                            color: darkMode ? white.main : dark.main,
                          },
                        })}
                        color="info"
                        variant="text"
                        count={pagination.pages}
                        page={currentPage}
                        onChange={(e, value) => setCurrentPage(value)}
                      />
                    </Stack>
                  </MDBox>
                )}
              </>
            ) : (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={5}
              >
                <MDTypography variant="h5" color="text.secondary">
                  {searchKey ? "No partners found matching your search" : "No partners found"}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default PartnerSettlement;
